import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mountain, LayoutGrid, Map, ShieldCheck, LogOut, Menu, HelpCircle, Lock, PlayCircle } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { hasRole, roleLabel } from "../lib/roles";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const NavBar = () => {
  const { user, logout, sites, mySites, rolesCatalog, selectedSite, setSelectedSite, openTour } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const links = [
    { to: "/", label: "Accueil", icon: LayoutGrid },
    { to: "/vue-carriere", label: "Vue carrière", icon: Map },
  ];
  if (hasRole(user, "RESPONSABLE", "ADMINISTRATEUR")) {
    links.push({ to: "/admin", label: "Administration", icon: ShieldCheck });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <header data-testid="app-navbar" className="sticky top-0 z-40 border-b border-slate-800 bg-[#0A0D12]/95 backdrop-blur-md print:hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo-link">
          <Mountain className="w-6 h-6 text-cyan-400" />
          <span className="font-black uppercase tracking-tight text-sm sm:text-base text-slate-100 leading-none">
            Fronts<span className="text-cyan-400">Carrière</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname === l.to || (l.to === "/admin" && location.pathname.startsWith("/admin"));
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${
                  active ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
                }`}
              >
                <l.icon className="w-4 h-4" /> {l.label}
              </Link>
            );
          })}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {hasRole(user, "ADMINISTRATEUR") ? (
            <select
              data-testid="nav-site-selector"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-md h-8 px-2 text-xs font-mono uppercase tracking-widest text-cyan-300"
            >
              <option value="TOUS">Tous les sites</option>
              {sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : mySites.length > 1 ? (
            <select
              data-testid="nav-site-selector"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-md h-8 px-2 text-xs font-mono uppercase tracking-widest text-cyan-300"
            >
              <option value="TOUS">Tous mes sites</option>
              {mySites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <div data-testid="nav-site-badge" className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-slate-700 rounded-md px-2 py-1">
              Site: <span className="text-cyan-400">{user.Site_Usine}</span>
            </div>
          )}
          <div className="text-right leading-tight">
            <div className="text-xs font-semibold text-slate-200" data-testid="nav-user-name">{user.Nom}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-500">{(user.Roles || []).map((r) => roleLabel(r, rolesCatalog)).join(" / ")}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="nav-help-menu-trigger" className="text-slate-400 hover:text-cyan-400">
                <HelpCircle className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121820] border-slate-700 text-slate-200">
              <DropdownMenuItem data-testid="nav-help-replay-tour" onClick={openTour} className="cursor-pointer text-xs uppercase tracking-wide gap-2">
                <PlayCircle className="w-4 h-4" /> Revoir le tutoriel
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-help-link" onClick={() => navigate("/aide")} className="cursor-pointer text-xs uppercase tracking-wide gap-2">
                <HelpCircle className="w-4 h-4" /> Aide
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-privacy-link" onClick={() => navigate("/confidentialite")} className="cursor-pointer text-xs uppercase tracking-wide gap-2">
                <Lock className="w-4 h-4" /> Confidentialité
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button" className="text-slate-400 hover:text-red-400">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <button className="md:hidden text-slate-300" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          <Menu className="w-6 h-6" />
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-800 px-4 py-3 space-y-1 bg-[#0A0D12]">
          {hasRole(user, "ADMINISTRATEUR") ? (
            <select
              data-testid="nav-site-selector-mobile"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded-md h-9 px-2 text-xs font-mono uppercase tracking-widest text-cyan-300"
            >
              <option value="TOUS">Tous les sites</option>
              {sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : mySites.length > 1 ? (
            <select
              data-testid="nav-site-selector-mobile"
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded-md h-9 px-2 text-xs font-mono uppercase tracking-widest text-cyan-300"
            >
              <option value="TOUS">Tous mes sites</option>
              {mySites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <div data-testid="nav-site-badge-mobile" className="mb-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-slate-700 rounded-md px-2 py-1.5 text-center">
              Site: <span className="text-cyan-400">{user.Site_Usine}</span>
            </div>
          )}
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-slate-300 hover:bg-slate-800/60">
              <l.icon className="w-4 h-4" /> {l.label}
            </Link>
          ))}
          <button data-testid="nav-help-replay-tour-mobile" onClick={() => { setOpen(false); openTour(); }} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-slate-300 hover:bg-slate-800/60 w-full">
            <HelpCircle className="w-4 h-4" /> Revoir le tutoriel
          </button>
          <Link to="/aide" onClick={() => setOpen(false)} data-testid="nav-help-link-mobile" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-slate-300 hover:bg-slate-800/60">
            <HelpCircle className="w-4 h-4" /> Aide
          </Link>
          <Link to="/confidentialite" onClick={() => setOpen(false)} data-testid="nav-privacy-link-mobile" className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-slate-300 hover:bg-slate-800/60">
            <Lock className="w-4 h-4" /> Confidentialité
          </Link>
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold text-red-400 w-full">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      )}
    </header>
  );
};
